import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography
} from '@mui/material';
import { CEP } from 'cep-promise';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '~/components/Input';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import { useContractLoader } from '~/hooks/useContractLoader';
import { ContractPostAddress } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { searchAddressByZipcode } from '~/utils/searchAddressByZipcode';
import { container, containerButton, containerForm, headerContainer } from './styles';
import schema from './validation';

export default function HelpZipcodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [useBuyerData, setUseBuyerData] = useState(false);

  // Hook para carregar dados do contrato
  const { contract_id } = useContractLoader({ stepName: 'step2' });

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    getValues,
    formState: { errors, dirtyFields }
  } = useForm<ContractPostAddress>({
    resolver: yupResolver(schema),
    defaultValues: {
      additional_details: '',
      city: '',
      country: '',
      neighborhood: '',
      number: '',
      state: '',
      street: '',
      zipcode: ''
    }
  });

  const {
    contractAsyncPostAddress,
    setZipcode,
    zipcode,
    city,
    state,
    neighborhood,
    street,
    number,
    insured
  } = useStore((store) => ({
    contractAsyncPostAddress: store.contractAsyncPostAddress,
    setZipcode: store.setZipcode,
    zipcode: store.quote.zipcode,
    city: store.quote.city,
    state: store.quote.state,
    neighborhood: store.quote.neighborhood,
    street: store.quote.street,
    number: store.quote.number,
    insured: store.insured
  }));

  const handleChangeUseBuyerData = () => {
    const newState = !useBuyerData;

    if (newState) {
      // Sempre preferir endereço do contrato (insured.address) quando existir (vindo do GET do contrato)
      const address = insured?.address;
      const hasInsuredAddress = address?.zipcode || address?.street || address?.city || address?.state;
      if (hasInsuredAddress && address) {
        setValue('city', address.city ?? '');
        setValue('state', address.state ?? '');
        setValue('neighborhood', address.neighborhood ?? '');
        setValue('street', address.street ?? '');
        setValue('zipcode', address.zipcode ?? '');
        setValue('number', address.number ?? '');
        setValue('additional_details', address.additional_details ?? '');
        setUseBuyerData(newState);
        return;
      }
      // Fallback: endereço da cotação (quote)
      if (zipcode) {
        setValue('zipcode', zipcode);
        setValue('city', city ?? '');
        setValue('state', state ?? '');
        setValue('neighborhood', neighborhood ?? '');
        setValue('street', street ?? '');
        setValue('number', number ?? '');
        setValue('additional_details', '');
        setUseBuyerData(newState);
        return;
      }
      storeState.addToast({
        title: 'Não foi possível encontrar os Dados do Comprador.',
        message: 'Carregue a página do contrato ou preencha o CEP para utilizar esta opção.',
        type: 'warning'
      });
      return;
    }

    setUseBuyerData(newState);
    reset();
  };

  const handleChangeZipCode = (value: string) => {
    const cleanZipCode = value.replaceAll('-', '').trim();

    const onSuccess = (params: CEP) => {
      setValue('city', params.city);
      setValue('state', params.state);
      setValue('neighborhood', params.neighborhood);
      setValue('street', params.street);
      setValue('zipcode', params.cep);
    };

    const onError = () => {
      storeState.addToast({
        title: 'Não foi possível encontrar o CEP',
        message: 'O CEP indicado não  foi encontrado',
        type: 'warning'
      });
    };

    if (cleanZipCode.length === 8) {
      searchAddressByZipcode(value, onSuccess, onError);
    }
  };

  const onSubmit: SubmitHandler<ContractPostAddress> = async (data) => {
    if (contract_id) {
      setLoading(true);
      try {
        const orgParam = searchParams.get('org');
        await contractAsyncPostAddress(
          contract_id,
          {
            street: typeof data.street === 'string' ? data.street.trim() : data.street,
            number: String(data.number),
            additional_details:
              typeof data.additional_details === 'string' ? data.additional_details.trim() : data.additional_details,
            neighborhood: typeof data.neighborhood === 'string' ? data.neighborhood.trim() : data.neighborhood,
            city: typeof data.city === 'string' ? data.city.trim() : data.city,
            state: typeof data.state === 'string' ? data.state.trim() : data.state,
            zipcode: data.zipcode,
            country: 'Brasil'
          },
          orgParam ?? undefined
        );
        setLoading(false);
        setZipcode(getValues('zipcode'));

        const contract_hash_id = searchParams.get('cid');
        const source = searchParams.get('source');
        const params = new URLSearchParams();
        if (contract_hash_id) params.set('cid', contract_hash_id);
        if (source) params.set('source', source);
        if (orgParam) params.set('org', orgParam);

        navigate(`/contract/equipment?${params.toString()}`);
      } catch (error: any) {
        setLoading(false);

        logErrorWithContext(error, 'contract_step2_address', {
          contractId: contract_id,
          zipcode: data.zipcode,
          hasCompleteAddress: !!(data.street && data.neighborhood && data.city)
        });

        const errorResponse = getErrorMessage(error);
        storeState.addToast(errorResponse);
      }
    } else {
      storeState.addToast({
        title: 'Não foi possível recuperar o contrato.',
        message: '',
        type: 'warning'
      });
    }
  };

  return (
    <Container maxWidth="lg" sx={container}>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Card>
            <CardContent>
              <Box sx={headerContainer}>
                <Typography variant="h6" align="center">
                  Dados para contrato
                </Typography>
              </Box>
              <Divider variant="middle" />
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={containerForm}>
                  <Typography sx={{ fontSize: { xs: 16, sm: 20 } }} variant="h6" fontWeight="bold">
                    2 - Endereço completo (pernoite)
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginTop: 1
                      }}
                      control={<Checkbox checked={useBuyerData} onChange={handleChangeUseBuyerData} color="success" />}
                      label={
                        <Typography component={'span'} sx={{ fontSize: { xs: 14, sm: 16 } }}>
                          Utilizar as mesmas informações do “Dados de Comprador”
                        </Typography>
                      }
                    />
                  </FormGroup>
                  <Controller
                    name="zipcode"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="CEP"
                          placeholder="XXXXX-XXX"
                          mask="zipcode"
                          error={errors?.zipcode ? true : false}
                          isDirty={dirtyFields.zipcode}
                          helperText={errors?.zipcode?.message}
                          {...field}
                          onChange={(event) => {
                            handleChangeZipCode(event.target.value);
                            field.onChange(event);
                          }}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Estado/UF (sigla)"
                          placeholder={'Digite primeiro o CEP'}
                          inputProps={{ maxLength: 2 }}
                          error={errors?.state ? true : false}
                          isDirty={dirtyFields.state}
                          helperText={errors?.state?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Cidade"
                          placeholder={'Digite primeiro o CEP'}
                          error={errors?.city ? true : false}
                          isDirty={dirtyFields.city}
                          helperText={errors?.city?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="neighborhood"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Bairro"
                          placeholder={'Digite primeiro o CEP'}
                          error={errors?.neighborhood ? true : false}
                          isDirty={dirtyFields.neighborhood}
                          helperText={errors?.neighborhood?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="street"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Endereço"
                          placeholder={'Digite primeiro o CEP'}
                          error={errors?.street ? true : false}
                          isDirty={dirtyFields.street}
                          helperText={errors?.street?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Controller
                        name="number"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              label="Número"
                              placeholder="XXX"
                              error={errors?.number ? true : false}
                              isDirty={dirtyFields.number}
                              helperText={errors?.number?.message}
                              {...field}
                            />
                          </>
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="additional_details"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              label="Complemento"
                              placeholder="Apto ou bloco"
                              error={errors?.additional_details ? true : false}
                              isDirty={dirtyFields.additional_details}
                              helperText={errors?.additional_details?.message}
                              {...field}
                            />
                          </>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={containerButton}>
                  <LoadingButton sx={{ fontSize: { xs: 16, sm: 20 } }} loading={loading} type="submit">
                    salvar e sair
                  </LoadingButton>
                  <LoadingButton
                    data-testid="contract-step2-next"
                    sx={{ fontSize: { xs: 16, sm: 20 } }}
                    loading={loading}
                    variant="contained"
                    type="submit"
                  >
                    Próximo passo
                  </LoadingButton>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          sx={{
            display: {
              xs: 'none',
              lg: 'block'
            }
          }}
        >
          <Card>
            <CardContent>
              <SidebarMesages showProduct={true} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
